import React, { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { discoveryAPI } from '../../services/api';
import toast from 'react-hot-toast';
import UserAvatar from '../../components/UI/UserAvatar';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  GlobeAltIcon,
  PhoneIcon,
  MapPinIcon,
  PlusIcon,
  BuildingOfficeIcon,
  LinkIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  UserIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  AdjustmentsHorizontalIcon,
  UsersIcon,
  CurrencyDollarIcon,
  TagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Modal from '../../components/UI/Modal';

// Industry options for search filter
const INDUSTRY_OPTIONS = [
  'Technology', 'Computer Software', 'Information Technology', 'Internet',
  'Healthcare', 'Hospital & Health Care', 'Medical Devices', 'Pharmaceuticals', 'Biotechnology',
  'Finance', 'Financial Services', 'Banking', 'Insurance', 'Investment Management',
  'Manufacturing', 'Industrial Automation', 'Mechanical Engineering',
  'Retail', 'E-Commerce', 'Consumer Goods', 'Apparel & Fashion',
  'Real Estate', 'Construction', 'Architecture & Planning',
  'Education', 'Higher Education', 'E-Learning',
  'Consulting', 'Management Consulting', 'Human Resources',
  'Marketing', 'Marketing & Advertising', 'Public Relations',
  'Legal', 'Law Practice',
  'Food & Beverage', 'Restaurants', 'Hospitality',
  'Transportation', 'Logistics & Supply Chain', 'Automotive',
  'Energy', 'Oil & Energy', 'Renewables & Environment',
  'Telecommunications', 'Wireless',
  'Aerospace', 'Defense & Space', 'Aviation',
  'Agriculture', 'Farming',
  'Media', 'Entertainment', 'Music', 'Publishing',
  'Government', 'Nonprofit Organization Management',
  'Design', 'Graphic Design',
];

// Employee count range options (Apollo format)
const EMPLOYEE_RANGE_OPTIONS = [
  { label: '1-10', value: '1,10' },
  { label: '11-20', value: '11,20' },
  { label: '21-50', value: '21,50' },
  { label: '51-100', value: '51,100' },
  { label: '101-200', value: '101,200' },
  { label: '201-500', value: '201,500' },
  { label: '501-1,000', value: '501,1000' },
  { label: '1,001-2,000', value: '1001,2000' },
  { label: '2,001-5,000', value: '2001,5000' },
  { label: '5,001-10,000', value: '5001,10000' },
  { label: '10,000+', value: '10001,' },
];

// Revenue range options
const REVENUE_RANGE_OPTIONS = [
  { label: 'Under $1M', value: '0,1000000' },
  { label: '$1M - $10M', value: '1000000,10000000' },
  { label: '$10M - $50M', value: '10000000,50000000' },
  { label: '$50M - $100M', value: '50000000,100000000' },
  { label: '$100M - $500M', value: '100000000,500000000' },
  { label: '$500M - $1B', value: '500000000,1000000000' },
  { label: '$1B+', value: '1000000000,' },
];

// Seniority levels for employee filtering
const SENIORITY_OPTIONS = [
  { label: 'C-Suite', value: 'c_suite' },
  { label: 'VP', value: 'vp' },
  { label: 'Director', value: 'director' },
  { label: 'Manager', value: 'manager' },
  { label: 'Senior', value: 'senior' },
  { label: 'Entry', value: 'entry' },
  { label: 'Owner / Founder', value: 'owner' },
];

// Department options for employee filtering
const DEPARTMENT_OPTIONS = [
  'engineering_technical',
  'sales',
  'marketing',
  'finance',
  'human_resources',
  'operations',
  'information_technology',
  'legal',
  'support',
  'product_management',
  'consulting',
  'data_science',
  'design',
  'education',
  'media_and_communication',
];

const Discovery = () => {
  // --- Search & filter state ---
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedEmployeeRanges, setSelectedEmployeeRanges] = useState([]);
  const [selectedRevenueRanges, setSelectedRevenueRanges] = useState([]);
  const [filterKeywords, setFilterKeywords] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // --- Results & pagination ---
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // --- Add-to-lead modal ---
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [matchingOrgs, setMatchingOrgs] = useState([]);
  const [addStep, setAddStep] = useState('checking');
  const [chosenOrgId, setChosenOrgId] = useState(null);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [contactForm, setContactForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', jobTitle: '',
  });

  // eslint-disable-next-line no-unused-vars
  const [addedCompanies, setAddedCompanies] = useState(new Set());
  const [addedByMap, setAddedByMap] = useState({});

  // --- Card & employee state ---
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [employeeData, setEmployeeData] = useState({});
  const [loadingMoreEmployees, setLoadingMoreEmployees] = useState({});
  const [revealingContacts, setRevealingContacts] = useState({});

  // Employee filters per company: { apolloOrgId: { seniority: '', department: '', title: '' } }
  const [employeeFilters, setEmployeeFilters] = useState({});

  const toggleCard = useCallback((id) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const queryClient = useQueryClient();

  // Fetch added-by info for results
  const fetchAddedByInfo = useCallback(async (companies) => {
    if (!companies || companies.length === 0) return;
    try {
      const apolloOrgIds = companies.map(c => c.apolloOrgId).filter(Boolean);
      const companyNames = companies.map(c => c.name).filter(Boolean);
      const response = await discoveryAPI.checkAdded({ apolloOrgIds, companyNames });
      const { byOrgId = {}, byName = {} } = response.data.data || {};
      setAddedByMap(prev => ({ ...prev, ...byOrgId, ...byName }));
    } catch (err) {
      // Non-critical
    }
  }, []);

  // --- Search handler ---
  const handleSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    const kws = filterKeywords ? filterKeywords.split(',').map(k => k.trim()).filter(Boolean) : [];
    const locs = location ? [location.trim()] : [];

    if (!keyword && selectedIndustries.length === 0 && kws.length === 0 && locs.length === 0) {
      toast.error('Please enter a keyword, industry, location, or tag');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    setCurrentPage(1);

    try {
      const response = await discoveryAPI.search({
        keyword: keyword || undefined,
        locations: locs.length > 0 ? locs : undefined,
        employeeRanges: selectedEmployeeRanges.length > 0 ? selectedEmployeeRanges : undefined,
        industryKeywords: selectedIndustries.length > 0 ? selectedIndustries : undefined,
        revenueRanges: selectedRevenueRanges.length > 0 ? selectedRevenueRanges : undefined,
        keywords: kws.length > 0 ? kws : undefined,
        page: 1,
        perPage: 25,
      });
      const searchResults = response.data.data || [];
      const pagination = response.data.pagination || {};
      setResults(searchResults);
      setTotalPages(pagination.totalPages || 1);
      setTotalResults(pagination.total || searchResults.length);
      await fetchAddedByInfo(searchResults);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Search failed. Please try again.');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [keyword, location, selectedIndustries, selectedEmployeeRanges, selectedRevenueRanges, filterKeywords, fetchAddedByInfo]);

  // Load more (next page)
  const handleLoadMore = async () => {
    if (currentPage >= totalPages || loadingMore) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    try {
      const kws = filterKeywords ? filterKeywords.split(',').map(k => k.trim()).filter(Boolean) : [];
      const locs = location ? [location.trim()] : [];
      const response = await discoveryAPI.search({
        keyword: keyword || undefined,
        locations: locs.length > 0 ? locs : undefined,
        employeeRanges: selectedEmployeeRanges.length > 0 ? selectedEmployeeRanges : undefined,
        industryKeywords: selectedIndustries.length > 0 ? selectedIndustries : undefined,
        revenueRanges: selectedRevenueRanges.length > 0 ? selectedRevenueRanges : undefined,
        keywords: kws.length > 0 ? kws : undefined,
        page: nextPage,
        perPage: 25,
      });
      const newResults = response.data.data || [];
      const pagination = response.data.pagination || {};
      setResults(prev => [...prev, ...newResults]);
      setCurrentPage(nextPage);
      setTotalPages(pagination.totalPages || totalPages);
      await fetchAddedByInfo(newResults);
    } catch (error) {
      toast.error('Failed to load more results');
    } finally {
      setLoadingMore(false);
    }
  };

  // Fetch employees for a company from Apollo
  const fetchEmployees = useCallback(async (company, filters = {}) => {
    const key = company.apolloOrgId;
    setEmployeeData(prev => ({ ...prev, [key]: { employees: [], loading: true, loaded: false, pagination: null } }));
    try {
      const domain = company.domain || (company.website
        ? company.website.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
        : null);
      const body = {
        companyName: company.name,
        companyDomain: domain,
        page: 1,
        perPage: 25,
      };
      if (filters.seniority) body.personSeniorities = [filters.seniority];
      if (filters.department) body.personDepartments = [filters.department];
      if (filters.title) body.personTitles = [filters.title];

      const response = await discoveryAPI.searchEmployees(body);
      setEmployeeData(prev => ({
        ...prev,
        [key]: {
          employees: response.data.data || [],
          loading: false,
          loaded: true,
          pagination: response.data.pagination || null,
        },
      }));
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to fetch employees';
      setEmployeeData(prev => ({
        ...prev,
        [key]: { employees: [], loading: false, loaded: true, error: msg, pagination: null },
      }));
    }
  }, []);

  // Load more employees for a company
  const loadMoreEmployees = useCallback(async (company) => {
    const key = company.apolloOrgId;
    const current = employeeData[key];
    if (!current?.pagination) return;
    const nextPage = current.pagination.page + 1;
    if (nextPage > current.pagination.totalPages) return;

    setLoadingMoreEmployees(prev => ({ ...prev, [key]: true }));
    try {
      const domain = company.domain || (company.website
        ? company.website.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
        : null);
      const filters = employeeFilters[key] || {};
      const body = {
        companyName: company.name,
        companyDomain: domain,
        page: nextPage,
        perPage: 25,
      };
      if (filters.seniority) body.personSeniorities = [filters.seniority];
      if (filters.department) body.personDepartments = [filters.department];
      if (filters.title) body.personTitles = [filters.title];

      const response = await discoveryAPI.searchEmployees(body);
      setEmployeeData(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          employees: [...(prev[key]?.employees || []), ...(response.data.data || [])],
          pagination: response.data.pagination || prev[key]?.pagination,
        },
      }));
    } catch (error) {
      toast.error('Failed to load more employees');
    } finally {
      setLoadingMoreEmployees(prev => ({ ...prev, [key]: false }));
    }
  }, [employeeData, employeeFilters]);

  // Reveal email
  const handleRevealEmail = useCallback(async (apolloId, companyId) => {
    const key = `email:${apolloId}`;
    setRevealingContacts(prev => ({ ...prev, [key]: true }));
    try {
      const response = await discoveryAPI.revealEmail({ apolloId });
      const revealed = response.data.data;
      setEmployeeData(prev => {
        const companyData = prev[companyId];
        if (!companyData) return prev;
        return {
          ...prev,
          [companyId]: {
            ...companyData,
            employees: companyData.employees.map(emp =>
              emp.apolloId === apolloId
                ? {
                    ...emp,
                    email: revealed.email,
                    lastName: revealed.lastName || emp.lastName,
                    firstName: revealed.firstName || emp.firstName,
                    linkedinUrl: revealed.linkedinUrl || emp.linkedinUrl,
                    emailRevealed: true,
                  }
                : emp
            ),
          },
        };
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reveal email');
    } finally {
      setRevealingContacts(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  // Reveal phone
  const handleRevealPhone = useCallback(async (apolloId, companyId) => {
    const key = `phone:${apolloId}`;
    setRevealingContacts(prev => ({ ...prev, [key]: true }));
    try {
      const response = await discoveryAPI.revealPhone({ apolloId });
      const revealed = response.data.data;
      setEmployeeData(prev => {
        const companyData = prev[companyId];
        if (!companyData) return prev;
        return {
          ...prev,
          [companyId]: {
            ...companyData,
            employees: companyData.employees.map(emp =>
              emp.apolloId === apolloId
                ? {
                    ...emp,
                    phone: revealed.phone,
                    lastName: revealed.lastName || emp.lastName,
                    firstName: revealed.firstName || emp.firstName,
                    linkedinUrl: revealed.linkedinUrl || emp.linkedinUrl,
                    phoneRevealed: true,
                  }
                : emp
            ),
          },
        };
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reveal phone');
    } finally {
      setRevealingContacts(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  // Add to leads mutation
  const addLeadMutation = useMutation({
    mutationFn: discoveryAPI.addLead,
    onSuccess: () => {
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries(['organizations']);
      toast.success(`Lead added successfully for "${selectedCompany?.name}"`);
      setAddedCompanies(prev => new Set([...prev, selectedCompany?.apolloOrgId]));
      fetchAddedByInfo(results);
      closeAddModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to add lead');
    },
  });

  // Open add-to-leads modal for a specific employee
  const handleAddEmployeeToLeads = async (company, employee) => {
    setSelectedCompany(company);
    setSelectedEmployee(employee);
    setIsAddModalOpen(true);
    setAddStep('checking');
    setChosenOrgId(null);
    setMatchingOrgs([]);
    setContactForm({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      phone: employee.phone || '',
      jobTitle: employee.title || '',
    });

    try {
      const response = await discoveryAPI.checkOrg({ name: company.name, website: company.website });
      const orgs = response.data.data || [];
      setMatchingOrgs(orgs);
      setAddStep(orgs.length > 0 ? 'choose' : 'contact-form');
    } catch {
      setAddStep('contact-form');
    }
  };

  // Open add-to-leads modal for manual entry
  const handleAddManualToLeads = async (company) => {
    setSelectedCompany(company);
    setSelectedEmployee(null);
    setIsAddModalOpen(true);
    setAddStep('checking');
    setChosenOrgId(null);
    setMatchingOrgs([]);
    setContactForm({
      firstName: '', lastName: '', email: '', phone: company.phone || '', jobTitle: '',
    });

    try {
      const response = await discoveryAPI.checkOrg({ name: company.name, website: company.website });
      const orgs = response.data.data || [];
      setMatchingOrgs(orgs);
      setAddStep(orgs.length > 0 ? 'choose' : 'contact-form');
    } catch {
      setAddStep('contact-form');
    }
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setSelectedCompany(null);
    setSelectedEmployee(null);
    setAddStep('checking');
    setMatchingOrgs([]);
    setChosenOrgId(null);
  };

  const handleChooseExisting = (orgId) => { setChosenOrgId(orgId); setAddStep('contact-form'); };
  const handleChooseNew = () => { setChosenOrgId(null); setAddStep('contact-form'); };

  const getAddedByUsers = useCallback((company) => {
    if (!company) return [];
    if (company.apolloOrgId && addedByMap[company.apolloOrgId]) return addedByMap[company.apolloOrgId];
    const nameKey = company.name?.toLowerCase()?.trim();
    if (nameKey && addedByMap[nameKey]) return addedByMap[nameKey];
    return [];
  }, [addedByMap]);

  const handleSubmitLead = () => {
    if (!selectedCompany) return;
    const payload = {
      companyName: selectedCompany.name,
      companyAddress: selectedCompany.address,
      companyPhone: selectedCompany.phone,
      companyWebsite: selectedCompany.website,
      companyIndustry: selectedCompany.industry || '',
      contactFirstName: contactForm.firstName || undefined,
      contactLastName: contactForm.lastName || undefined,
      contactEmail: contactForm.email || undefined,
      contactPhone: contactForm.phone || undefined,
      contactJobTitle: contactForm.jobTitle || undefined,
      apolloOrgId: selectedCompany.apolloOrgId,
    };
    if (chosenOrgId) { payload.existingOrgId = chosenOrgId; } else { payload.createNewOrg = true; }
    addLeadMutation.mutate(payload);
  };

  // Helper: update employee filter for a company
  const updateEmployeeFilter = (companyId, field, value) => {
    setEmployeeFilters(prev => ({
      ...prev,
      [companyId]: { ...(prev[companyId] || {}), [field]: value },
    }));
  };

  // Active filters count
  const activeFilterCount = selectedIndustries.length + selectedEmployeeRanges.length + selectedRevenueRanges.length + (filterKeywords ? 1 : 0);

  const clearAllFilters = () => {
    setSelectedIndustries([]);
    setSelectedEmployeeRanges([]);
    setSelectedRevenueRanges([]);
    setFilterKeywords('');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Discovery</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Search Apollo.io for companies and add them as leads to your pipeline
          </p>
        </div>
      </div>

      {/* Search Form */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-surface-800 shadow-sm rounded-2xl shadow-card border border-gray-200 dark:border-surface-700 p-4 space-y-3"
      >
        <form onSubmit={handleSearch} className="flex items-end gap-3">
          {/* Company Name / Keyword */}
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Company Name</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="e.g. I-2-I, Salesforce..." className="glass-input pl-9" />
            </div>
          </div>

          {/* Location */}
          <div className="w-48 flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Location</label>
            <div className="relative">
              <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, state, or country..." className="glass-input pl-9" />
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg border transition-all h-[42px] ${
              showAdvancedFilters || activeFilterCount > 0
                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                : 'bg-white dark:bg-surface-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-600'
            }`}
          >
            <AdjustmentsHorizontalIcon className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-primary-600 text-white rounded-full">{activeFilterCount}</span>
            )}
          </button>

          {/* Search Button */}
          <button type="submit" disabled={isSearching} className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed h-[42px]">
            {isSearching ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <MagnifyingGlassIcon className="w-4 h-4" />}
            Search
          </button>
        </form>

        {/* Advanced Filters Panel */}
        <AnimatePresence>
          {showAdvancedFilters && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
              <div className="pt-3 pb-2 border-t border-gray-100 dark:border-gray-700 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Advanced Filters</span>
                  {activeFilterCount > 0 && (
                    <button onClick={clearAllFilters} className="text-xs text-red-500 hover:text-red-600 font-medium">Clear all</button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Industry */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      <TagIcon className="w-3 h-3 inline mr-1" />Industry
                    </label>
                    <select value={selectedIndustries[0] || ''} onChange={(e) => setSelectedIndustries(e.target.value ? [e.target.value] : [])} className="glass-input text-sm">
                      <option value="">All Industries</option>
                      {INDUSTRY_OPTIONS.map((ind) => (<option key={ind} value={ind}>{ind}</option>))}
                    </select>
                  </div>

                  {/* Employee Count */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      <UsersIcon className="w-3 h-3 inline mr-1" />Employees
                    </label>
                    <select value={selectedEmployeeRanges[0] || ''} onChange={(e) => setSelectedEmployeeRanges(e.target.value ? [e.target.value] : [])} className="glass-input text-sm">
                      <option value="">Any Size</option>
                      {EMPLOYEE_RANGE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>

                  {/* Revenue */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      <CurrencyDollarIcon className="w-3 h-3 inline mr-1" />Revenue
                    </label>
                    <select value={selectedRevenueRanges[0] || ''} onChange={(e) => setSelectedRevenueRanges(e.target.value ? [e.target.value] : [])} className="glass-input text-sm">
                      <option value="">Any Revenue</option>
                      {REVENUE_RANGE_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>

                  {/* Keywords / Tags */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      <TagIcon className="w-3 h-3 inline mr-1" />Keywords
                    </label>
                    <input type="text" value={filterKeywords} onChange={(e) => setFilterKeywords(e.target.value)} placeholder="SaaS, AI, cloud..." className="glass-input text-sm" />
                  </div>
                </div>

                {/* Active filter pills */}
                {activeFilterCount > 0 && (
                  <div className="flex items-center flex-wrap gap-1.5 pt-1">
                    {selectedIndustries.map(ind => (
                      <span key={ind} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs rounded-full">
                        {ind}
                        <button onClick={() => setSelectedIndustries(prev => prev.filter(i => i !== ind))}><XMarkIcon className="w-3 h-3" /></button>
                      </span>
                    ))}
                    {selectedEmployeeRanges.map(r => (
                      <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                        {EMPLOYEE_RANGE_OPTIONS.find(o => o.value === r)?.label || r} employees
                        <button onClick={() => setSelectedEmployeeRanges(prev => prev.filter(i => i !== r))}><XMarkIcon className="w-3 h-3" /></button>
                      </span>
                    ))}
                    {selectedRevenueRanges.map(r => (
                      <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
                        {REVENUE_RANGE_OPTIONS.find(o => o.value === r)?.label || r}
                        <button onClick={() => setSelectedRevenueRanges(prev => prev.filter(i => i !== r))}><XMarkIcon className="w-3 h-3" /></button>
                      </span>
                    ))}
                    {filterKeywords && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full">
                        Tags: {filterKeywords}
                        <button onClick={() => setFilterKeywords('')}><XMarkIcon className="w-3 h-3" /></button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Results */}
      {isSearching && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-surface-800 shadow-sm rounded-2xl shadow-card border border-gray-200 dark:border-surface-700 p-5 animate-pulse">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-4" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
            </div>
          ))}
        </div>
      )}

      {!isSearching && hasSearched && results.length === 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white dark:bg-surface-800 shadow-sm rounded-2xl shadow-card border border-gray-200 dark:border-surface-700 p-12 text-center">
          <BuildingOfficeIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No companies found</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Try adjusting your search criteria or filters</p>
        </motion.div>
      )}

      {!isSearching && results.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing <span className="font-semibold text-gray-900 dark:text-white">{results.length}</span>
              {totalResults > results.length && <> of <span className="font-semibold text-gray-900 dark:text-white">{totalResults.toLocaleString()}</span></>} companies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(() => {
              const numCols = 3;
              const cols = Array.from({ length: numCols }, () => []);
              results.forEach((item, i) => cols[i % numCols].push(item));
              return cols.map((col, colIndex) => (
                <div key={colIndex} className="flex flex-col gap-4">
                  {col.map((company) => {
                    const addedByUsers = getAddedByUsers(company);
                    const compKey = company.apolloOrgId;
                    return (
                <motion.div key={compKey} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-surface-800 shadow-sm rounded-2xl shadow-card border border-gray-200 dark:border-surface-700 hover:shadow-lg transition-all group overflow-hidden">
                  {/* Clickable Header */}
                  <button type="button" onClick={() => toggleCard(compKey)} className="w-full text-left px-5 pt-4 pb-3 flex items-start gap-3">
                    {/* Logo or icon — onError handles broken/txt logos */}
                    {company.logoUrl && !company.logoUrl.endsWith('.txt') ? (
                      <img src={company.logoUrl} alt="" className="w-9 h-9 rounded-xl object-contain bg-gray-50 dark:bg-surface-700 flex-shrink-0 mt-0.5 p-0.5"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }} />
                    ) : null}
                    <div className={`w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/30 items-center justify-center flex-shrink-0 mt-0.5 ${company.logoUrl && !company.logoUrl.endsWith('.txt') ? 'hidden' : 'flex'}`}>
                      <BuildingOfficeIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary-600 transition-colors">
                          {company.name}
                        </h3>
                      </div>
                      {company.industry && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{company.industry}</p>
                      )}
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        {company.address && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            <MapPinIcon className="w-3 h-3 inline mr-0.5 -mt-0.5" />{company.address}
                          </p>
                        )}
                        {company.employeeCount && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            <UsersIcon className="w-3 h-3 inline mr-0.5 -mt-0.5" />{company.employeeCount.toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {addedByUsers.length > 0 && (
                        <div className="flex items-center -space-x-1.5" title={`Added by: ${addedByUsers.map(u => `${u.firstName} ${u.lastName}`).join(', ')}`}>
                          {addedByUsers.slice(0, 3).map((u) => (
                            <UserAvatar key={u.id} user={u} size="2xs" className="ring-2 ring-white dark:ring-surface-800" />
                          ))}
                          {addedByUsers.length > 3 && (
                            <span className="w-5 h-5 rounded-full bg-gray-200 dark:bg-surface-600 text-[9px] font-medium text-gray-600 dark:text-gray-300 flex items-center justify-center ring-2 ring-white dark:ring-surface-800">
                              +{addedByUsers.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      {company.annualRevenue && (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400">{company.annualRevenue}</span>
                      )}
                      <motion.div animate={{ rotate: expandedCards.has(compKey) ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      </motion.div>
                    </div>
                  </button>

                  {/* Collapsible Details */}
                  <AnimatePresence initial={false}>
                    {expandedCards.has(compKey) && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} className="overflow-hidden">
                        <div className="px-5 pb-4 space-y-2">
                          <div className="border-t border-gray-100 dark:border-gray-700 mb-3" />

                          {/* Quick info badges */}
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {company.industry && (
                              <span className="px-2 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-surface-700 text-gray-600 dark:text-gray-300 rounded-full">{company.industry}</span>
                            )}
                            {company.employeeCount && (
                              <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 rounded-full">{company.employeeCount.toLocaleString()} employees</span>
                            )}
                            {company.annualRevenue && (
                              <span className="px-2 py-0.5 text-[10px] font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300 rounded-full">{company.annualRevenue} revenue</span>
                            )}
                            {company.foundedYear && (
                              <span className="px-2 py-0.5 text-[10px] font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300 rounded-full">Founded {company.foundedYear}</span>
                            )}
                          </div>

                          {/* Description */}
                          {company.shortDescription && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{company.shortDescription}</p>
                          )}

                          {/* Phone */}
                          {company.phone && (
                            <div className="flex items-center gap-2">
                              <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <a href={`tel:${company.phone}`} className="text-xs text-gray-600 dark:text-gray-400 hover:text-primary-600 transition-colors">{company.phone}</a>
                            </div>
                          )}

                          {/* Website */}
                          {company.website && (
                            <div className="flex items-center gap-2">
                              <GlobeAltIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:text-primary-700 truncate transition-colors">
                                {company.domain || company.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                              </a>
                            </div>
                          )}

                          {/* LinkedIn */}
                          {company.linkedinUrl && (
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-[#0A66C2] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                              <a href={company.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0A66C2] hover:text-[#004182] transition-colors truncate">
                                LinkedIn Profile
                              </a>
                            </div>
                          )}

                          {/* Technologies/Keywords */}
                          {company.keywords && company.keywords.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {company.keywords.slice(0, 8).map((kw, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 text-[9px] bg-gray-100 dark:bg-surface-700 text-gray-500 dark:text-gray-400 rounded">{kw}</span>
                              ))}
                              {company.keywords.length > 8 && <span className="text-[9px] text-gray-400">+{company.keywords.length - 8}</span>}
                            </div>
                          )}

                          {/* Employees Section */}
                          <div className="mt-3 p-3 bg-gray-50/80 dark:bg-surface-700/50 rounded-xl border border-gray-200 dark:border-gray-700/40">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <UserIcon className="w-4 h-4 text-gray-400" />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Employees</span>
                                {employeeData[compKey]?.pagination?.total > 0 && (
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                    ({employeeData[compKey].employees.length} of {employeeData[compKey].pagination.total.toLocaleString()})
                                  </span>
                                )}
                              </div>
                              {!employeeData[compKey]?.loaded && (
                                <button onClick={() => fetchEmployees(company, employeeFilters[compKey] || {})} disabled={employeeData[compKey]?.loading}
                                  className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors disabled:opacity-50">
                                  {employeeData[compKey]?.loading ? (<><ArrowPathIcon className="w-3 h-3 animate-spin" />Searching...</>) : (<><MagnifyingGlassIcon className="w-3 h-3" />Find Employees</>)}
                                </button>
                              )}
                            </div>

                            {/* Employee Filters */}
                            <div className="space-y-1.5 mb-2">
                              <div className="flex gap-2">
                                <select value={employeeFilters[compKey]?.seniority || ''} onChange={(e) => updateEmployeeFilter(compKey, 'seniority', e.target.value)}
                                  className="flex-1 text-[10px] px-2 py-1 bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300">
                                  <option value="">All Seniority</option>
                                  {SENIORITY_OPTIONS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                                </select>
                                <select value={employeeFilters[compKey]?.department || ''} onChange={(e) => updateEmployeeFilter(compKey, 'department', e.target.value)}
                                  className="flex-1 text-[10px] px-2 py-1 bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300">
                                  <option value="">All Departments</option>
                                  {DEPARTMENT_OPTIONS.map(d => (<option key={d} value={d}>{d.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>))}
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={employeeFilters[compKey]?.title || ''}
                                  onChange={(e) => updateEmployeeFilter(compKey, 'title', e.target.value)}
                                  placeholder="Job title (e.g. CEO, Engineer...)"
                                  className="flex-1 text-[10px] px-2 py-1 bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 placeholder-gray-400"
                                />
                                {employeeData[compKey]?.loaded && (
                                  <button onClick={() => {
                                    setEmployeeData(prev => ({ ...prev, [compKey]: undefined }));
                                    setTimeout(() => fetchEmployees(company, employeeFilters[compKey] || {}), 50);
                                  }} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors" title="Re-search with filters">
                                    <ArrowPathIcon className="w-3 h-3" />
                                    <span>Search</span>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Loading state */}
                            {employeeData[compKey]?.loading && (
                              <div className="space-y-2">
                                {[1, 2, 3].map(i => (
                                  <div key={i} className="flex items-center gap-2 animate-pulse">
                                    <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600" />
                                    <div className="flex-1">
                                      <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2 mb-1" />
                                      <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-1/3" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Error */}
                            {employeeData[compKey]?.error && (<p className="text-xs text-red-500 dark:text-red-400">{employeeData[compKey].error}</p>)}

                            {/* Employee list */}
                            {employeeData[compKey]?.loaded && !employeeData[compKey]?.error && (
                              <>
                                {employeeData[compKey].employees.length === 0 ? (
                                  <p className="text-xs text-gray-400 dark:text-gray-500">No employees found for this company</p>
                                ) : (
                                  <div className="space-y-2 max-h-80 overflow-y-auto">
                                    {employeeData[compKey].employees.map((emp) => (
                                      <div key={emp.apolloId} className="flex items-start gap-2.5 p-2 bg-white dark:bg-surface-800 rounded-lg border border-gray-100 dark:border-gray-700/50">
                                        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-primary-600 dark:text-primary-400">
                                          {(emp.firstName?.[0] || '').toUpperCase()}{(emp.lastName?.[0] || '').toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{emp.firstName} {emp.lastName}</p>
                                          {emp.title && (<p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{emp.title}</p>)}
                                          <div className="mt-1.5 flex items-center gap-2">
                                            {/* Email */}
                                            {emp.emailRevealed && emp.email ? (
                                              <span className="inline-flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300 truncate max-w-[140px]" title={emp.email}>
                                                <EnvelopeIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /><span className="truncate">{emp.email}</span>
                                              </span>
                                            ) : emp.emailRevealed ? (
                                              <span title="No email found"><EnvelopeIcon className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" /></span>
                                            ) : emp.hasEmail ? (
                                              <button onClick={() => handleRevealEmail(emp.apolloId, compKey)} disabled={revealingContacts[`email:${emp.apolloId}`]}
                                                className="text-gray-400 hover:text-blue-500 transition-colors disabled:opacity-50" title="Reveal email">
                                                {revealingContacts[`email:${emp.apolloId}`] ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <EnvelopeIcon className="w-3.5 h-3.5" />}
                                              </button>
                                            ) : (
                                              <span title="Email not available"><EnvelopeIcon className="w-3.5 h-3.5 text-gray-200 dark:text-gray-700" /></span>
                                            )}
                                            {/* Phone */}
                                            {emp.phoneRevealed && emp.phone ? (
                                              <span className="inline-flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300" title={emp.phone}>
                                                <PhoneIcon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" /><span>{emp.phone}</span>
                                              </span>
                                            ) : emp.phoneRevealed ? (
                                              <span title="No phone found"><PhoneIcon className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" /></span>
                                            ) : emp.hasPhone ? (
                                              <button onClick={() => handleRevealPhone(emp.apolloId, compKey)} disabled={revealingContacts[`phone:${emp.apolloId}`]}
                                                className="text-gray-400 hover:text-blue-500 transition-colors disabled:opacity-50" title="Reveal phone">
                                                {revealingContacts[`phone:${emp.apolloId}`] ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <PhoneIcon className="w-3.5 h-3.5" />}
                                              </button>
                                            ) : (
                                              <span title="Phone not available"><PhoneIcon className="w-3.5 h-3.5 text-gray-200 dark:text-gray-700" /></span>
                                            )}
                                            {/* LinkedIn */}
                                            {emp.linkedinUrl ? (
                                              <a href={emp.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-[#0A66C2] hover:text-[#004182] transition-colors" title="View LinkedIn profile">
                                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                                </svg>
                                              </a>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                          <button onClick={() => handleAddEmployeeToLeads(company, emp)}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700/50 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                                            title="Add as lead">
                                            <PlusIcon className="w-3 h-3" />Lead
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}

                            {/* Load More Employees */}
                            {employeeData[compKey]?.loaded && !employeeData[compKey]?.error && employeeData[compKey]?.pagination &&
                              employeeData[compKey].pagination.page < employeeData[compKey].pagination.totalPages && (
                              <button onClick={() => loadMoreEmployees(company)} disabled={loadingMoreEmployees[compKey]}
                                className="w-full mt-2 inline-flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-700/50 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-700 transition-colors disabled:opacity-50">
                                {loadingMoreEmployees[compKey] ? (<><ArrowPathIcon className="w-3 h-3 animate-spin" />Loading...</>) : (<><ChevronDownIcon className="w-3 h-3" />Load More Employees</>)}
                              </button>
                            )}

                            {/* Prompt if not yet searched */}
                            {!employeeData[compKey] && (
                              <div className="flex items-center gap-2">
                                {[1, 2, 3].map(i => (<div key={i} className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 opacity-40" />))}
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">Click "Find Employees" to search</span>
                              </div>
                            )}
                          </div>

                          {/* Added By Indicator */}
                          {addedByUsers.length > 0 && (
                            <div className="mt-2 p-2.5 bg-primary-50 dark:bg-primary-900/15 border border-primary-100 dark:border-primary-800/30 rounded-xl">
                              <div className="flex items-center gap-2">
                                <UserIcon className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
                                <span className="text-xs font-medium text-primary-700 dark:text-primary-300">Added by:</span>
                              </div>
                              <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                                {addedByUsers.map((u) => (
                                  <div key={u.id} className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 bg-white dark:bg-surface-700 rounded-full border border-primary-200 dark:border-primary-700/50" title={`${u.firstName} ${u.lastName}`}>
                                    <UserAvatar user={u} size="2xs" />
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">{u.firstName} {u.lastName}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="pt-3 border-t border-gray-100 dark:border-gray-700 mt-3">
                            <button onClick={() => handleAddManualToLeads(company)}
                              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-surface-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-surface-600 transition-all">
                              <BriefcaseIcon className="w-4 h-4" />Manual Add to Leads
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
                    );
                  })}
                </div>
              ));
            })()}
          </div>

          {/* Load More */}
          {currentPage < totalPages && (
            <div className="flex justify-center pt-2">
              <button onClick={handleLoadMore} disabled={loadingMore}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-surface-700 transition-all disabled:opacity-50">
                {loadingMore ? (<><ArrowPathIcon className="w-4 h-4 animate-spin" />Loading...</>) : (`Load More Results (Page ${currentPage} of ${totalPages})`)}
              </button>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!hasSearched && !isSearching && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-white dark:bg-surface-800 shadow-sm rounded-2xl shadow-card border border-gray-200 dark:border-surface-700 p-16 text-center">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MagnifyingGlassIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Discover New Leads</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Search Apollo.io for companies by name, industry, location, employee count, revenue, and more.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mt-1">
            Find potential leads and add them to your pipeline with one click.
          </p>
        </motion.div>
      )}

      {/* Add to Leads Modal */}
      <Modal isOpen={isAddModalOpen} onClose={closeAddModal}
        title={selectedEmployee
          ? `Add "${selectedEmployee.firstName} ${selectedEmployee.lastName}" as Lead`
          : `Add "${selectedCompany?.name || ''}" as Lead`}
        size="lg">
        <div className="space-y-4">
          {addStep === 'checking' && (
            <div className="flex flex-col items-center py-8">
              <ArrowPathIcon className="w-8 h-8 text-primary-500 animate-spin mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Checking for existing organizations...</p>
            </div>
          )}

          {addStep === 'choose' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl">
                <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Similar organization(s) found</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    We found existing organizations that match "{selectedCompany?.name}".
                    You can link this lead to an existing organization or create a new one.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Existing Organizations:</p>
                {matchingOrgs.map((org) => (
                  <button key={org._id} onClick={() => handleChooseExisting(org._id)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-surface-700 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:border-primary-300 dark:hover:border-primary-700 transition-all text-left group">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <BuildingOfficeIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{org.name}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {org.industry && (<span className="text-xs text-gray-500 dark:text-gray-400">{org.industry}</span>)}
                          {org.website && (<span className="text-xs text-gray-400 dark:text-gray-500 truncate">{org.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-primary-600 dark:text-primary-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">Link to this</span>
                      <LinkIcon className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
              <div className="relative flex items-center py-2">
                <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                <span className="px-4 text-xs text-gray-400 uppercase">or</span>
                <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
              </div>
              <button onClick={handleChooseNew}
                className="w-full flex items-center justify-center gap-2 p-4 bg-white dark:bg-surface-700 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-all text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                <PlusIcon className="w-4 h-4" />Create as New Organization
              </button>
            </div>
          )}

          {addStep === 'contact-form' && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-surface-700 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BuildingOfficeIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedCompany?.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{chosenOrgId ? 'Linking to existing organization' : 'Will create new organization'}</p>
                  </div>
                </div>
                {selectedCompany?.address && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 ml-13 pl-[52px]">{selectedCompany.address}</p>
                )}
              </div>
              {matchingOrgs.length > 0 && (
                <button onClick={() => setAddStep('choose')} className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
                  ← Change organization selection
                </button>
              )}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Contact Details {selectedEmployee ? (
                    <span className="text-primary-500 font-normal">(pre-filled from employee)</span>
                  ) : (
                    <span className="text-gray-400 font-normal">(fill in contact info)</span>
                  )}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">First Name</label>
                    <input type="text" value={contactForm.firstName} onChange={(e) => setContactForm(prev => ({ ...prev, firstName: e.target.value }))}
                      placeholder="Contact first name" className="w-full px-3 py-2 bg-white dark:bg-surface-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Last Name</label>
                    <input type="text" value={contactForm.lastName} onChange={(e) => setContactForm(prev => ({ ...prev, lastName: e.target.value }))}
                      placeholder="Contact last name" className="w-full px-3 py-2 bg-white dark:bg-surface-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                    <input type="email" value={contactForm.email} onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="contact@company.com" className="w-full px-3 py-2 bg-white dark:bg-surface-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Phone</label>
                    <input type="text" value={contactForm.phone} onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+1 234 567 890" className="w-full px-3 py-2 bg-white dark:bg-surface-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Job Title</label>
                    <input type="text" value={contactForm.jobTitle} onChange={(e) => setContactForm(prev => ({ ...prev, jobTitle: e.target.value }))}
                      placeholder="e.g. CEO, Marketing Manager..." className="w-full px-3 py-2 bg-white dark:bg-surface-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button onClick={closeAddModal} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-700 rounded-xl transition-all">Cancel</button>
                <button onClick={handleSubmitLead} disabled={addLeadMutation.isLoading}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl shadow-sm transition-all disabled:opacity-50">
                  {addLeadMutation.isLoading ? (<><ArrowPathIcon className="w-4 h-4 animate-spin" />Adding...</>) : (<><PlusIcon className="w-4 h-4" />Add to Leads</>)}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Discovery;
